// LocationShapeLayer — die Verortungs-Grafik (Verbindungslinien + Umkreis-Kreis) als EIN Canvas-OverlayView im markerLayer,
// garantiert ÜBER den getilten Bild-Overlays. Zeichnet EINE Form (Editor) oder N Formen (Gallery, read-only) auf denselben
// Canvas. Löst das verstreute Polygon/Circle-Konstrukt ab (google.maps.Polygon/Circle liegen auf Googles Vektor-Canvas und
// lassen sich per API NICHT über das Bild-Overlay heben; ein OverlayView-Canvas im markerLayer schon).
//
// Komposition (wie ProjectionHelper): die Klasse HÄLT eine google.maps.OverlayView und überschreibt onAdd/draw/onRemove;
// `google` ist Runtime-Global (nach Maps-API-Load). ES-Klasse mit #-Feldern (Modul-Regel: google-lazy OverlayView-Kern).
//
// Schritt 1 (Chef-Charge 8 / Phase location-shape-layer): NUR Anzeige der Formen (Linien + Kreis), pointerEvents:none,
// KEINE Editierbarkeit (Punkt-Handles/Drag folgen in Schritt 2). Die Formen-Mathe ist 1:1 aus dem EDITOR-Zweig von
// drawPoly (basic.js, drawPoly OHNE `frontend`) portiert — NICHT die Frontend/Gallery-Variante (die zeichnet den
// 3-Punkt-Sichtkegel anders; Schritt-0-Befund). Punkt-Marker (1-Punkt-Fall) bleiben vorerst native google.maps.Marker.
//
// Interface: setShapes(shapes) · setPoints(points) (1-Form-Bequemlichkeit, Editor) · getShapes()/getPoints() · redraw() ·
// destroy().  shapes = [{ points:[{lat,lng},…] }, …]; je Form 1..5 Punkte. Editor = 1 Form (draggbar), Gallery = N (read-only).

export class LocationShapeLayer {
    #map;
    #overlay;
    #canvas = null;
    #ctx = null;
    #shapes = [];   // [{ points: [{lat,lng}, …] }] — je Form 1..5 Punkte. Editor = 1 Form, Gallery = N.
    #strokeColor = null;
    #fillColor = null;

    constructor(map, options = {}) {
        this.#map = map;
        this.#resolveColors();   // Akzent-basierte Farben aus CSS auflösen (Canvas kann keine CSS-Vars/color-mix direkt)
        this.#overlay = new google.maps.OverlayView();
        this.#overlay.onAdd = () => this.#onAdd();
        this.#overlay.draw = () => this.#draw();
        this.#overlay.onRemove = () => this.#onRemove();
        this.#overlay.setMap(map);
    }

    // Setzt N Formen (Gallery: alle Viewpoints) und zeichnet neu. shapes = [{ points: [{lat,lng}, …] }, …].
    setShapes(shapes) {
        this.#shapes = Array.isArray(shapes)
            ? shapes.map((s) => ({ points: Array.isArray(s && s.points) ? s.points.slice() : [] }))
            : [];
        this.redraw();
        return this;
    }

    // Editor-Bequemlichkeit: EINE Form aus einer Punkt-Liste (Basis-Koordinaten, KEINE berechneten Spike-Enden).
    setPoints(points) {
        return this.setShapes(points && points.length ? [{ points }] : []);
    }

    getShapes() { return this.#shapes.map((s) => ({ points: s.points.slice() })); }

    getPoints() { return this.#shapes[0] ? this.#shapes[0].points.slice() : []; }

    // Neu zeichnen (nach setPoints; Google ruft draw() bei Pan/Zoom eh selbst).
    redraw() { if (this.#canvas) { this.#draw(); } }

    destroy() {
        if (this.#overlay) { this.#overlay.setMap(null); }   // triggert onRemove → Canvas raus
        this.#overlay = null;
        this.#canvas = null;
        this.#ctx = null;
        this.#shapes = [];
    }

    // Farben aus dem Legacy-Locate-Cyan (#24F4FF, basic.js drawingOptions) — das ganze Marker-Konstrukt (Marker, Linien,
    // Fill) ist cyan, weil es auf mehr Karten-Untergründen leuchtet als der Archiv-Akzent.
    // Linien = pures Cyan (NICHT abgedunkelt — den Kontrast/Rand tragen die weiß umrandeten Marker); Fill = Cyan bei 20%
    // Deckkraft (wie Legacy-fillOpacity 0.2). Canvas versteht kein color-mix → für den Fill ein Probe-Element rechnen lassen.
    #resolveColors() {
        const LOCATE_CYAN = '#24F4FF';
        const probe = document.createElement('span');
        probe.style.position = 'absolute';
        probe.style.visibility = 'hidden';
        document.body.appendChild(probe);
        const resolve = (expr) => { probe.style.color = expr; return getComputedStyle(probe).color; };
        this.#strokeColor = LOCATE_CYAN;   // pures Cyan, keine Abdunklung
        this.#fillColor = resolve('color-mix(in srgb, ' + LOCATE_CYAN + ' 20%, transparent)');
        probe.remove();
    }

    #onAdd() {
        const c = document.createElement('canvas');
        c.style.position = 'absolute';
        c.style.left = '0';
        c.style.top = '0';
        c.style.zIndex = '50000';         // über dem getilten Overlay, aber UNTER den Punkt-Markern (zIndex 100000)
        c.style.pointerEvents = 'none';   // reine Anzeige (Interaktion kommt in Schritt 2) — Klicks gehen an die Marker durch
        this.#canvas = c;
        this.#ctx = c.getContext('2d');
        const panes = this.#overlay.getPanes();
        // markerLayer (Pane 2): liegt über dem overlayLayer (Pane 1, wo die getilten Overlays + Vektor-Polygone sind), also
        // über dem Overlay; die Punkt-Marker (zIndex 100000) im selben Pane bleiben mit ihrem höheren zIndex ÜBER den Linien.
        if (panes) { panes.markerLayer.appendChild(c); }
    }

    #onRemove() {
        if (this.#canvas && this.#canvas.parentNode) { this.#canvas.parentNode.removeChild(this.#canvas); }
        this.#canvas = null;
        this.#ctx = null;
    }

    #draw() {
        const proj = this.#overlay && this.#overlay.getProjection();
        if (!proj || !this.#canvas || !this.#ctx) { return; }

        // Canvas = Karten-Viewport: Ursprung (oben-links) in Div-Pixel projizieren, Canvas dorthin + Viewport-Größe.
        // Formen, die über den Rand ragen (die Sichtkegel-Rays), clippen am Canvas-Rand — genau wie auf der Karte.
        const mapDiv = this.#map.getDiv();
        const w = mapDiv.offsetWidth;
        const h = mapDiv.offsetHeight;
        const originLatLng = proj.fromContainerPixelToLatLng(new google.maps.Point(0, 0));
        const origin = originLatLng ? proj.fromLatLngToDivPixel(originLatLng) : null;
        if (!origin) { return; }
        this.#canvas.style.left = origin.x + 'px';
        this.#canvas.style.top = origin.y + 'px';
        // HiDPI/Retina: backing store in device pixels, CSS box in logical pixels, then draw in logical pixels via a
        // dpr-scaled transform. Ohne das rendert der Browser die 1:1-Canvas hochskaliert → die Striche werden pixelig.
        const dpr = window.devicePixelRatio || 1;
        const bw = Math.round(w * dpr), bh = Math.round(h * dpr);
        if (this.#canvas.width !== bw) { this.#canvas.width = bw; }
        if (this.#canvas.height !== bh) { this.#canvas.height = bh; }
        this.#canvas.style.width = w + 'px';
        this.#canvas.style.height = h + 'px';

        const ctx = this.#ctx;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);   // draw in logical px; backing store is device px
        ctx.clearRect(0, 0, w, h);
        if (!this.#shapes.length) { return; }

        ctx.strokeStyle = this.#strokeColor;
        ctx.fillStyle = this.#fillColor;
        ctx.lineWidth = 2;
        ctx.lineJoin = 'round';

        // Jede Form einzeln: Basis-Punkte → lokale Canvas-Koordinaten (Div-Pixel − Ursprung), dann zeichnen.
        // <2 Punkte = nur Origin-Marker (nativ), keine Form.
        for (const shape of this.#shapes) {
            if (!shape.points || shape.points.length < 2) { continue; }
            const pts = shape.points.map((p) => {
                const dp = proj.fromLatLngToDivPixel(new google.maps.LatLng(p.lat, p.lng));
                return { x: dp.x - origin.x, y: dp.y - origin.y };
            });
            this.#strokeShape(ctx, pts);
        }
    }

    // Editor-Mathe (drawPoly OHNE frontend): 2 = Radius-Linie + Umkreis; 3 = Sichtkegel; 4/5 = Polygon über feste Index-Reihen.
    // Flächen bekommen Fill + Rand; die Radius-Linie nur Rand.
    #strokeShape(ctx, pts) {
        const n = pts.length;
        if (n === 2) {
            const r = Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y);   // Radius = Pixel-Abstand p0→p1 (Kreis durch p1)
            ctx.beginPath(); ctx.arc(pts[0].x, pts[0].y, r, 0, 2 * Math.PI); ctx.fill(); ctx.stroke();
            ctx.beginPath(); ctx.moveTo(pts[0].x, pts[0].y); ctx.lineTo(pts[1].x, pts[1].y); ctx.stroke();
        } else if (n === 3) {
            this.#fillStrokePath(ctx, this.#conePath(pts));
        } else if (n === 4 || n === 5) {
            const idx = (n === 4) ? [0, 1, 2, 0] : [0, 1, 2, 4, 3, 1, 2, 0];
            this.#fillStrokePath(ctx, idx.map((i) => pts[i]));
        }
    }

    // 3-Punkt-Sichtkegel (basic.js:2723-2753, else-Zweig): multiplier = 20*zoom; ab zoom>6 Spikes (Summenrichtung +
    // Roh-Vektor-Verlängerungen ab p1/p2) + immer das Basis-Dreieck p0→p2→p1→p0. Liefert den Pixel-Pfad.
    #conePath([p0, p1, p2]) {
        const zoom = this.#map.getZoom();
        const mult = 20 * zoom;
        const anx = p1.x - p0.x, any = p1.y - p0.y;
        const bnx = p2.x - p0.x, bny = p2.y - p0.y;
        const aE = Math.hypot(anx, any) || 1, bE = Math.hypot(bnx, bny) || 1;
        const path = [];
        if (zoom > 6) {
            path.push(
                p0,
                { x: p0.x + anx / aE * mult + bnx / bE * mult, y: p0.y + any / aE * mult + bny / bE * mult },   // Summen-Spike (normiert)
                p0,
                { x: p1.x + anx * mult, y: p1.y + any * mult },   // Ray ab p1 (Roh-Vektor)
                p0,
                { x: p2.x + bnx * mult, y: p2.y + bny * mult }    // Ray ab p2 (Roh-Vektor)
            );
        }
        path.push(p0, p2, p1, p0);
        return path;
    }

    // Pfad füllen (Akzent 20% Deckkraft) + umranden (Akzent 20% dunkler). Die Spikes umschließen 0 Fläche → gefüllt wird nur die Basis.
    #fillStrokePath(ctx, path) {
        if (!path.length) { return; }
        ctx.beginPath();
        ctx.moveTo(path[0].x, path[0].y);
        for (let i = 1; i < path.length; i++) { ctx.lineTo(path[i].x, path[i].y); }
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
    }
}
